/* eslint-disable react/prop-types */
import React, { useEffect, useState, useContext, useMemo } from "react";
import debounce from "lodash/debounce";
import TableInstance from "./table-instance";
import PropTypes from "prop-types";
import { useLocation, useSearchParams, useParams } from "react-router-dom";
import { GetSortBy } from "../../utils/sortby";
import { QuotationsData } from "../../contexts";
import { COLUMNS_FUCTIONALITIES } from "./Constants";
// import { ALL_QUOTATIONS_TYPE, PRESORTED_QUOTATIONS_TYPE, STRIPS_QUOTATIONS_TYPE } from "../../utils/constants";
import GetAllQuotations from "../../hooks/fetch-hooks/fetch-all/get-all-quotations";
import { DEFAULT_PAGE_SIZE, LEGACY_DEFAULT_PAGE_SIZE } from "../../v2/constants/pagination";
import { consoleForPath } from "../../v2/constants/consoles";

const SEARCH_DEBOUNCE_MS = 300;

async function Response(pageindex, pagelimit, search, searchByQuoteId, searchByQuoteAmount, searchByPartnerEntityId, order, orderBy, userId) {
    let response;
    // switch (quotationType) {
    //     case ALL_QUOTATIONS_TYPE:
    //         response = await GetAllQuotations({
    //             page: pageindex,
    //             size: pagelimit,
    //             patientName: search,
    //             userName: search,
    //             order: order,
    //             orderBy: orderBy,
    //             userId: userId,
    //             type: 'ALL'
    //         }, '')
    //         break;
    //     case PRESORTED_QUOTATIONS_TYPE:
    //         response = await GetAllQuotations({
    //             page: pageindex,
    //             size: pagelimit,
    //             patientName: search,
    //             userName: search,
    //             order: order,
    //             orderBy: orderBy,
    //             userId: userId,
    //             type: 'PRESORTED'
    //         }, '')
    //         break;
    //     case STRIPS_QUOTATIONS_TYPE:
    //         response = await GetAllQuotations({
    //             page: pageindex,
    //             size: pagelimit,
    //             patientName: search,
    //             userName: search,
    //             order: order,
    //             orderBy: orderBy,
    //             userId: userId,
    //             type: 'STRIPS'
    //         }, '')
    //         break;
    //     default:
    //         response = await GetAllQuotations({
    //             page: pageindex,
    //             size: pagelimit,
    //             patientName: search,
    //             userName: search,
    //             order: order,
    //             orderBy: orderBy,
    //             userId: userId,
    //         }, '')
    //         break;
    // }

    const useNameSearch = !searchByQuoteId && !searchByQuoteAmount && !searchByPartnerEntityId;

    response = await GetAllQuotations({
        page: pageindex,
        size: pagelimit,
        quoteId: searchByQuoteId ? search : undefined,
        quoteValue: searchByQuoteAmount ? search : undefined,
        partnerEntityId: searchByPartnerEntityId ? search : undefined,
        patientName: useNameSearch ? search : undefined,
        userName: useNameSearch ? search : undefined,
        order: order,
        orderBy: orderBy,
        userId: userId,
    }, '')

    return response
}

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

/**
 * The page length to default to, decided by which shell this table is mounted in.
 *
 * This component backs the legacy /quotations route AND the ops console's Quotations
 * tab. Legacy has always paged at 50 and keeps doing so; the v2 console pages at 20 like
 * every other list in it. Mirrors the identical helper in orderTable/orders.jsx -- see
 * its comment for why this asks where it is rather than taking a prop.
 */
const useDefaultPageSize = () => {
    const { pathname } = useLocation();
    return consoleForPath(pathname) === "legacy" ? LEGACY_DEFAULT_PAGE_SIZE : DEFAULT_PAGE_SIZE;
};

function QuotationsTable() {
    const { quotationType } = useContext(QuotationsData);
    const defaultPageSize = useDefaultPageSize();
    const [daysLeft, setDaysLeft] = useState(30);
    const query = useQuery();
    // eslint-disable-next-line
    const [searchParams, setSearchParams] = useSearchParams()
    // Main States
    const { id } = useParams()
    const [tableData, setTableData] = React.useState([]);
    // eslint-disable-next-line no-unused-vars
    const [total, setTotal] = useState(0);
    const [pageSort, setPageSort] = React.useState([]);
    //  All States for query
    const [pageindex, setPageIndex] = React.useState(query.get('page') || 1);
    // eslint-disable-next-line
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [value, setValue] = React.useState(query.get('search') || "");
    const [debouncedSearch, setDebouncedSearch] = React.useState(query.get('search') || "");
    const [order, setOrder] = React.useState("DESC")
    const [orderBy, setOrderBy] = React.useState();
    const [searchByQuoteId, setSearchByQuoteId] = React.useState(false);
    const [searchByQuoteAmount, setSearchByQuoteAmount] = React.useState(false);
    const [searchByPartnerEntityId, setSearchByPartnerEntityId] = React.useState(false);
    // UseEffect Starts Here


    useEffect(() => {
        if (pageSort.length > 0) {
            setOrder(pageSort[0].desc ? "DESC" : "ASC")
            setOrderBy(GetSortBy(pageSort[0].id))
        }
        else if (pageSort.length < 1) {
            setOrder(undefined)
            setOrderBy(undefined)
        }
    }, [pageSort])

    const debouncedSetSearch = useMemo(
        () =>
            debounce((val) => {
                setDebouncedSearch(val);
                setPageIndex(1);
            }, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    const getData = async () => {
        let leftDays = daysLeft === '' ? 0 : daysLeft
        const fetchedData = await Response(pageindex, pageSize, debouncedSearch, searchByQuoteId, searchByQuoteAmount, searchByPartnerEntityId, order, orderBy, id, quotationType, leftDays);
        const newTableData = fetchedData?.data?.data?.rows.map((order) => {
            return {
                ...order,
                daysLeft: Math.floor(
                    (new Date(order.endDate) - new Date()) / (1000 * 60 * 60 * 24) + 2
                ),
                fulfillmentPercentage: order.fulfillment || 
                    (order.fulfillment !== undefined ? order.fulfillment : 90)
            };
        });
        setTableData(newTableData);
        setTotal(fetchedData?.data?.data?.count);
    };

    useEffect(() => {
        getData();
    }, [pageindex, pageSize, debouncedSearch, order, orderBy, daysLeft, quotationType, searchByQuoteId, searchByQuoteAmount, searchByPartnerEntityId]);


    useEffect(() => {
        setSearchParams({
            page: pageindex,
            size: pageSize,
            search: debouncedSearch,
        })
    }, [pageindex, pageSize, debouncedSearch]);


    const maxValues = Math.round(total / pageSize);

    const handleDeleteRow = (id) => {
        const newData = tableData.filter((item) => item.id !== id)
        setTableData(newData);
    }

    return (
        <>
            <div className="flex gap-4 items-center relative">
                <input
                    value={value}
                    className="px-6  font-sans w-[80%] bg-slate-100 py-4"
                    onChange={(e) => {
                        const next = e.target.value;
                        setValue(next);
                        debouncedSetSearch(next);
                    }}
                    placeholder={`${total} records...`}
                    style={{
                        fontSize: "1.1rem",
                        border: "0",
                    }}
                />
                <select
                    value={
                        searchByQuoteId ? "id" : searchByQuoteAmount ? "amount" : searchByPartnerEntityId ? "partnerEntityId" : ""
                    }
                    onChange={(e) => {
                        const v = e.target.value;
                        debouncedSetSearch.cancel();
                        setSearchByQuoteId(v === "id");
                        setSearchByQuoteAmount(v === "amount");
                        setSearchByPartnerEntityId(v === "partnerEntityId");
                        setDebouncedSearch(value);
                        setPageIndex(1);
                    }}
                    className="px-4 py-2 rounded-lg border border-slate-300 bg-white font-sans"
                >
                    <option value="">Search By...</option>
                    <option value="id">Quote ID</option>
                    <option value="amount">Quote Amount</option>
                    <option value="partnerEntityId">Partner Entity ID</option>
                </select>
                {COLUMNS_FUCTIONALITIES[quotationType].includes("DaysLeft") && < div
                    className="flex justify-end items-center"
                >
                    <input
                        type="number"
                        onChange={(e) => { setDaysLeft(e.target.value) }}
                        className="focus:outline-none border-2 border-gray-300 rounded-2xl font-sans"
                        value={daysLeft}
                        style={{ zIndex: "0" }}
                    />
                </div>}

            </div >
            {/* <div className="flex justify-start items-center my-[1rem] gap-2" >
                <div>
                    <button onClick={() => setQuotationType(ALL_QUOTATIONS_TYPE)} className={`${quotationType === ALL_QUOTATIONS_TYPE ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"} border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}>
                        All Quotations
                    </button>
                </div>
                <div>
                    <button onClick={() => setQuotationType(PRESORTED_QUOTATIONS_TYPE)} className={`${quotationType === PRESORTED_QUOTATIONS_TYPE ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"} border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}>
                        Presorted
                    </button>
                </div>
                <div>
                    <button onClick={() => setQuotationType(STRIPS_QUOTATIONS_TYPE)} className={`${quotationType === STRIPS_QUOTATIONS_TYPE ? "bg-elm-500 text-white-50" : "bg-transparent text-elm-500"} border-[1px] border-elm-500 px-4 hover:bg-elm-500 hover:text-white-50 font-semibold py-2 rounded-[9px]`}>
                        Strips
                    </button>
                </div>
            </div> */}
            <TableInstance
                tableData={tableData}
                pageCount={total}
                setPageSort={setPageSort}
                pageSort={pageSort}
                handleDeleteRow={handleDeleteRow}
                refetchOrders={getData}
            />
            {/* Btns For Pagination  */}
            <div className="btn_div">
                <button onClick={() => setPageIndex((count) => Number(count) - 1)} disabled={pageindex <= 1} className="previous">
                    Previous
                </button>
                <span className="pageindex">
                    {total < 49 ? "Page 1" : `Page ${pageindex} of ${maxValues}`}
                </span>
                <button
                    onClick={() => setPageIndex((count) => Number(count) + 1)}
                    disabled={pageindex >= maxValues}
                    className="next"
                >
                    Next {maxValues}
                </button>
            </div>

        </>
    );
}

export default QuotationsTable;

QuotationsTable.propTypes = {
    daysLeft: PropTypes.number,
};